import { useState, useCallback, useMemo } from 'react';
import { Button, Text, VStack } from '@chakra-ui/react';
import { useViewer } from '../common/ForgeViewer';
import { AlgorithmSettingsProps } from 'src/components/auto-shapes/types';
import { AutoShapeAlgorithmType, ElementWithProperties } from 'src/gql/graphql';
import { useSelection } from 'src/services/viewer/selection';

export function ExternalWallFinishes3DSettings({
  value,
  onChange,
  onValidityChange,
}: AlgorithmSettingsProps<AutoShapeAlgorithmType.ExternalWallFinishes_3D>) {
  const { viewer } = useViewer();
  const { selectedBimElements } = useSelection();
  const [selectedLeafCount, setSelectedLeafCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const modelUrn = useMemo(() => {
    return Object.keys(selectedBimElements)[0];
  }, [selectedBimElements]);

  const fetchLeafDbIds = useCallback(async () => {
    if (!viewer) return;

    setIsLoading(true);
    try {
      const dbIds = selectedBimElements[modelUrn] || [];

      // First get all leaf nodes and their reverse mapped IDs for each dbId
      const leafArraysWithProps = await Promise.all(
        dbIds.map(async (dbId) => {
          // Get the properties for the parent element
          const parentProps = await new Promise<ElementWithProperties>(
            (resolve) => {
              viewer.model.getProperties(dbId, (props) => {
                const category = props.properties.find(
                  (p) =>
                    p.displayCategory === '__category__' ||
                    p.displayName === 'IfcClass'
                )?.displayValue;

                const material = props.properties
                  .find((p) => p.displayName === 'IfcMaterial')
                  ?.displayValue?.toString()
                  ?.split(';')[0];

                resolve({
                  category: category as string,
                  material: material as string,
                  name: props.name as string,
                  dbId: dbId,
                });
              });
            }
          );

          // Get all leaf nodes for this dbId
          const leafIds = await getLeafDbIds(viewer, dbId);

          console.log('leafIds', leafIds);

          // Map each leaf node to include parent properties and reverse mapped ID as dbId
          return leafIds.map((leafId) => ({
            ...parentProps,
            dbId: viewer.model.reverseMapDbId(leafId),
          }));
        })
      );

      const flattenedElements = leafArraysWithProps.flat();
      setSelectedLeafCount(flattenedElements.length);
      onChange({
        ...value,
        elements: flattenedElements,
      });
      onValidityChange(true);
    } catch (error) {
      console.error('Error getting leaf dbIds:', error);
      onValidityChange(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    viewer,
    selectedBimElements,
    modelUrn,
    onChange,
    value,
    onValidityChange,
  ]);

  return (
    <VStack spacing={4} align="stretch">
      <Text>Selected leaf elements: {selectedLeafCount}</Text>
      <Button
        onClick={fetchLeafDbIds}
        isLoading={isLoading}
        loadingText="Fetching elements..."
      >
        Fetch Selected Elements
      </Button>
    </VStack>
  );
}

// Recursively get all leaf dbIds for a given dbId
const getLeafDbIds = async (
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): Promise<number[]> => {
  return new Promise((resolve) => {
    viewer.getProperties(dbId, (props) => {
      const childDbIds = props.properties
        .filter((p) => p.displayCategory === '__child__')
        .map((p) => parseInt(p.displayValue as string));

      if (childDbIds.length === 0) {
        // This is a leaf node
        resolve([dbId]);
      } else {
        // Get leaf nodes for all children
        Promise.all(
          childDbIds.map((childId) => getLeafDbIds(viewer, childId))
        ).then((results) => resolve(results.flat()));
      }
    });
  });
};
